import SwitchActivePair from '@/components/common/SwitchActivePair';
import ModuleWindow, { moduleHeaderClassName } from '@/components/mosaic/ModuleWindow';
import ModuleWindowControls from '@/components/mosaic/ModuleWindowControl';

import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectActiveMarketPair, selectMarketPairs } from '@/state/reducers/marketPairSlice';
import { selectMosaic } from '@/state/reducers/mosaicSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { selectAppliedTheme } from '@/state/reducers/themeSlice';
import { removeChartConfig, selectChartConfigs, setChartConfigs } from '@/state/reducers/workspaceSlice';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import useXplorSpotLayout, { tabIcon } from '@/utils/hooks/useXplorSpotLayout';
import useXplorTraderLayout from '@/utils/hooks/useXplorTraderLayout';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { LuBarChart } from 'react-icons/lu';
import { MdOpenInNew } from 'react-icons/md';
import AdvancedChartView from './AdvancedChartView';

const ChartModule = (props: any) => {
    const { id, tradingModule, isBaseStructure = false } = props;

    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const screen = useScreenSizes();

    const pairs = useAppSelector(selectMarketPairs);
    const mosaic = useAppSelector(selectMosaic);
    const activePair = useAppSelector(selectActiveMarketPair);
    const accountType = useAppSelector(selectAccountType);
    const chartConfigs = useAppSelector(selectChartConfigs);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);
    const appliedTheme = useAppSelector(selectAppliedTheme);

    const chartInstrument = useMemo(() => (chartConfigs && chartConfigs[id]) || undefined, [chartConfigs]);

    const instrument = useMemo(() => {
        return chartInstrument || activePair;
    }, [chartInstrument, activePair]);

    const onPopOut = useCallback(
        () => window.open(`/chart?symbol=${instrument?.netdania}`, '', 'width=1200,height=600'),
        [instrument]
    );

    const instrumentOptions = useMemo(() => {
        return pairs.map((instrument) => {
            const config: any = getInstrumentConfig(accountType, instrumentConfigs, instrument.celer);

            const show = config?.type === 'Future' && config.gcexSymbol ? config.gcexSymbol : instrument.show;

            return {
                label: show,
                value: instrument.celer,
                market: instrument,
                config
            };
        });
    }, [pairs, instrumentConfigs, accountType]);

    const [isMouseDown, setIsMouseDown] = useState<any>(false);
    const [forcedReload, setForcedReload] = useState<boolean>(false);

    useDidUpdate(() => {
        setForcedReload(true);
        setTimeout(() => {
            setForcedReload(false);
        }, 150);
    }, [appliedTheme]);

    useEffect(() => {
        const handleMouseDown = (event) => {
            if (event.button === 0) setIsMouseDown(true);
        };

        const handleMouseUp = (event) => {
            if (event.button === 0) setIsMouseDown(false);
        };

        document.querySelectorAll('.mosaic-split').forEach((element) => {
            element.addEventListener('mousedown', handleMouseDown);
            element.addEventListener('mouseup', handleMouseUp);
        });

        window.addEventListener('mouseup', handleMouseUp);

        // Cleanup: Remove event listeners when the component is unmounted
        return () => {
            document.querySelectorAll('.mosaic-split').forEach((element) => {
                element.removeEventListener('mousedown', handleMouseDown);
                element.removeEventListener('mouseup', handleMouseUp);
            });
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isMouseDown]);

    const shouldOverlayCharts = useMemo(
        () => isMouseDown || mosaic.isDragging || mosaic.isResizing,
        [isMouseDown, mosaic]
    );

    const { workspace: xplorSpotWorkspace } = useXplorSpotLayout();
    const { workspace: xplorTraderWorkspace } = useXplorTraderLayout();

    const isEnlarged = useMemo(() => {
        const workspace = location.pathname.includes('/trader') ? xplorTraderWorkspace : xplorSpotWorkspace;
        return workspace.enlargedModule === id;
    }, [location, xplorSpotWorkspace.enlargedModule, xplorTraderWorkspace.enlargedModule, id]);

    return (
        <ModuleWindow
            {...props}
            renderToolbar={() => (
                <div className={moduleHeaderClassName} dir={lang.dir}>
                    <div className="h-full flex items-center p-2 gap-2">
                        {tabIcon[tradingModule?.key || ''] && (
                            <div className="flex items-center justify-center bg-brand-neutral-700 rounded-full p-1">
                                {tabIcon[tradingModule?.key || '']({
                                    className: 'w-4 h-4',
                                    'aria-hidden': true
                                })}
                            </div>
                        )}
                        <span className="font-semibold whitespace-nowrap">{tradingModule?.title}</span>

                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <div
                                title="Open in Separate Window"
                                className={cn('p-2 rounded-sm flex items-center', {
                                    ['cursor-pointer hover:bg-brand-primary']: true
                                })}
                                onClick={onPopOut}>
                                <MdOpenInNew className="w-4 h-4" />
                            </div>
                        )}
                    </div>
                    <div className="flex h-full items-center">
                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <SwitchActivePair
                                selected={{
                                    label: instrument?.show || lang.tickers.instrumentSelector,
                                    value: instrument?.celer || undefined,
                                    market: undefined
                                }}
                                options={instrumentOptions}
                                onSubmit={(instrument) => {
                                    dispatch(setChartConfigs({ key: id, config: instrument }));
                                }}
                            />
                        )}
                        <ModuleWindowControls
                            {...props}
                            onClose={() => {
                                dispatch(removeChartConfig({ key: id }));
                                localStorage.removeItem(`chartState-${id}`);
                            }}
                        />
                    </div>
                </div>
            )}>
            <div className="h-full bg-brand-background">
                {shouldOverlayCharts && <div className="overlay h-full w-full absolute z-10" />}
                {instrument?.celer && !forcedReload && (
                    <Fragment>
                        {((isEnlarged && !isBaseStructure) || (!isEnlarged && isBaseStructure)) && (
                            <AdvancedChartView id={id} chartInstrument={instrument} />
                        )}
                    </Fragment>
                )}
                {!instrument?.celer && (
                    <div className="h-full bg-brand-background">
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-brand-neutral-500">
                            <LuBarChart className="w-16 h-16 mb-4 opacity-20" />
                            <p className="text-lg font-medium">Chart Module</p>
                            <p className="text-sm mb-6">Select an instrument to load the Chart</p>
                        </div>
                    </div>
                )}
            </div>
        </ModuleWindow>
    );
};

export default ChartModule;
